import { useEffect, useMemo, useState } from "react";
import { api, clearToken } from "../lib/api";
import TransactionsTable from "./TransactionsTable";
import PendingTransactions from "./PendingTransactions";
import SqlWorkbench from "./SqlWorkbench";
import NewTransactionModal from "./NewTransactionModal";
import TransactionModal from "./TransactionModal";
import AdminUsers from "./AdminUsers";
import AdminClients from "./AdminClients";
import AdminCategories from "./AdminCategories";
import Budget from "./Budget";
import CodesList from "./CodesList";
import PayeesList from "./PayeesList";
import PaymentsList from "./PaymentsList";
import FunctionsList from "./FunctionsList";
import CalendarList from "./CalendarList";
import SangriaIcon from "./SangriaIcon";
import ChangePasswordModal from "./ChangePasswordModal";
import { canCreateTransaction, canSeeTransactions, txnGrants, isSuperUser } from "../lib/txnPermissions";

const APP_NAME = "Sangria Studio";

const fmtDate = (iso) =>
  new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function BrowserTab({ fys, calSeq, setCalSeq, loadingFys, refreshFys, isAdmin, profile }) {
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [modalMode, setModalMode] = useState(null);
  const [editing, setEditing] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // Approvers/Payers/Admins land on their PENDING action queue; pure
  // inquiry-only viewers land on ALL (they have nothing to action).
  const g = txnGrants(profile);
  const writeCapable = isSuperUser(profile) || g.has("TXN_ADM") || g.has("TXN_APV") || g.has("TXN_PAY");
  const [subTab, setSubTab] = useState(writeCapable ? "pending" : "all");

  useEffect(() => { setSelectedTxn(null); }, [calSeq]);

  const selectedFy = useMemo(
    () => fys.find((f) => String(f.cal_seq) === String(calSeq)),
    [fys, calSeq]
  );

  // Stable fyRange reference so MonthlyTotals doesn't re-fetch on every parent render
  const fyRange = useMemo(
    () => (selectedFy ? { from: selectedFy.cal_frm, to: selectedFy.cal_to } : null),
    [selectedFy]
  );

  const downloadCsv = async () => {
    const res = await fetch(`${BACKEND}/api/transactions/export?cal_seq=${calSeq}`, {
      credentials: "include",
    });
    if (!res.ok) { alert(`Export failed: ${res.status}`); return; }
    const blob = await res.blob();
    const cd = res.headers.get("content-disposition") || "";
    const m = cd.match(/filename="([^"]+)"/);
    const name = m ? m[1] : `txnsaction_${calSeq}.csv`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {subTab === "all" && calSeq && selectedFy && (
        <TransactionsTable
          calSeq={calSeq}
          selectedTxn={selectedTxn}
          onSelect={setSelectedTxn}
          fyFrom={selectedFy.cal_frm}
          onExportCsv={downloadCsv}
          refreshKey={refreshKey}
          fys={fys}
          setCalSeq={setCalSeq}
          loadingFys={loadingFys}
          selectedFy={selectedFy}
          onNewTxn={canCreateTransaction(profile) ? () => { setEditing(null); setModalMode("new"); } : undefined}
          view={subTab}
          onChangeView={setSubTab}
        />
      )}
      {subTab === "pending" && (
        <PendingTransactions
          calSeq={calSeq}
          profile={profile}
          fyRange={fyRange}
          refreshKey={refreshKey}
          onChanged={() => setRefreshKey((k) => k + 1)}
          view={subTab}
          onChangeView={setSubTab}
        />
      )}
      {!calSeq && loadingFys && subTab === "all" && (
        <section><div className="card"><div className="loading-row"><span className="spinner" /> Loading…</div></div></section>
      )}

      {selectedTxn && fyRange && (
        <TransactionModal
          txn={selectedTxn}
          profile={profile}
          calSeq={calSeq}
          fyRange={fyRange}
          onClose={() => setSelectedTxn(null)}
          onSaved={() => {
            setSelectedTxn(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      {modalMode && (
        <NewTransactionModal
          defaultDate={selectedFy?.cal_to}
          editing={modalMode === "edit" ? editing : null}
          onClose={() => { setModalMode(null); setEditing(null); }}
          onCreated={() => {
            setModalMode(null);
            setEditing(null);
            setRefreshKey((k) => k + 1);
            refreshFys && refreshFys();
          }}
        />
      )}
    </>
  );
}

export default function Dashboard({ profile, onLogout }) {
  const [tab, setTab] = useState("calendar");
  const [fys, setFys] = useState([]);
  const [calSeq, setCalSeq] = useState("");
  const [loadingFys, setLoadingFys] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPwChange, setShowPwChange] = useState(false);

  const loadFys = () => api.fiscalYears().then((rows) => {
    setFys(rows);
    setCalSeq((cur) => {
      if (cur && rows.some((r) => String(r.cal_seq) === String(cur))) return cur;
      if (rows.length) {
        // 1) prefer the FY whose [cal_frm, cal_to] window contains today
        const today = new Date().toISOString().slice(0, 10);
        const containingToday = rows.find(
          (r) => String(r.cal_frm) <= today && today <= String(r.cal_to)
        );
        if (containingToday) return String(containingToday.cal_seq);
        // 2) else pick the FY with the most transactions
        const withData = [...rows]
          .filter((r) => (r.txn_count || 0) > 0)
          .sort((a, b) => (b.txn_count || 0) - (a.txn_count || 0));
        return String((withData[0] || rows[0]).cal_seq);
      }
      return "";
    });
  });

  useEffect(() => {
    loadFys().finally(() => setLoadingFys(false));
    document.title = APP_NAME;
  }, []);

  const logout = () => { clearToken(); onLogout(); };

  const displayName =
    profile && (profile.first_name || profile.last_name)
      ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim()
      : profile?.user_id || "";

  const goTo = (t) => { setTab(t); setMenuOpen(false); };

  const isAdmin = profile?.user_ty === "X";
  const isSuper = isSuperUser(profile);
  const grants = useMemo(() => new Set(profile?.functions || []), [profile?.functions]);
  const hasAny = (prefix) => isSuper || [...grants].some((g) => g.startsWith(`${prefix}_`));
  const hasWrite = (prefix) => isSuper || grants.has(`${prefix}_ADM`) || grants.has(`${prefix}_MOD`);

  // Page-level RBAC (2026-08-19): every page except Transactions follows the
  // generic {PREFIX}_ADM/_INQ/_MOD-or-SUPER rule (SQL Workbench uses its own
  // single SQL_ADM-or-SUPER grant, added 2026-08-24). Transactions has its
  // own bespoke TXN_ADM/INQ/APV/PAY model.
  const canSeeTxn      = canSeeTransactions(profile);
  const canSeeBudget   = hasAny("BUD");
  const canSeeCodes    = hasAny("COD");
  const canSeePayees   = hasAny("PYE");
  const canSeePayments = hasAny("PMT");
  const canSeeLedgers  = hasAny("LGR");
  const canSeeUsers    = hasAny("USR");
  const canSeeClients  = hasAny("CLI");
  const canSeeEvents   = hasAny("CAL");
  const canSeeSql      = isSuper || grants.has("SQL_ADM"); // 2026-08-24: userauth cutover, legacy user_ty='X' no longer applies

  const canWriteCodes    = hasWrite("COD");
  const canWritePayees   = hasWrite("PYE");
  const canWritePayments = hasWrite("PMT");
  const canWriteLedgers  = hasWrite("LGR");
  const canWriteUsers    = hasWrite("USR");
  const canWriteClients  = hasWrite("CLI");
  const canWriteEvents   = hasWrite("CAL");
  const canAdminEvents   = isSuper || grants.has("CAL_ADM");
  const canSeeFun        = hasAny("FUN");
  const canWriteFun      = hasWrite("FUN");

  // Menu order (2026-08-20): events, budgets, ledgers, transactions, codes,
  // payees, payments, clients, users, functions, then sql (unlisted, kept
  // last so it isn't dropped). User Auth is no longer a standalone page —
  // it's reached from the Users page's Edit-user → "User Auth" flow.
  const firstAvailableTab = useMemo(() => {
    const order = [
      ["calendar", canSeeEvents], ["budget", canSeeBudget], ["ledgers", canSeeLedgers],
      ["browse", canSeeTxn], ["codes", canSeeCodes], ["payees", canSeePayees],
      ["payments", canSeePayments], ["clients", canSeeClients], ["users", canSeeUsers],
      ["functions", canSeeFun], ["sql", canSeeSql],
    ];
    const found = order.find(([, ok]) => ok);
    return found ? found[0] : "none";
  }, [canSeeEvents, canSeeTxn, canSeeBudget, canSeeCodes, canSeePayees,
      canSeePayments, canSeeLedgers, canSeeUsers, canSeeClients, canSeeFun,
      canSeeSql]);

  // Snap the current tab to a safe value if the user can't access it
  useEffect(() => {
    const allowed = {
      calendar: canSeeEvents, browse: canSeeTxn, budget: canSeeBudget,
      codes: canSeeCodes, payees: canSeePayees, payments: canSeePayments,
      ledgers: canSeeLedgers, users: canSeeUsers, clients: canSeeClients,
      sql: canSeeSql, functions: canSeeFun,
    };
    if (tab in allowed && !allowed[tab]) setTab(firstAvailableTab);
  }, [tab, canSeeEvents, canSeeTxn, canSeeBudget, canSeeCodes, canSeePayees,
      canSeePayments, canSeeLedgers, canSeeUsers, canSeeClients, canSeeSql,
      canSeeFun, firstAvailableTab]);

  // Apply per-tenant theme color (client_clr) — paints --client-clr CSS var
  // so the topbar + primary buttons re-tint without re-rendering.
  useEffect(() => {
    const clr = profile?.client_clr;
    const root = document.documentElement;
    if (clr && /^[0-9A-Fa-f]{6}$/.test(clr)) {
      root.style.setProperty("--client-clr", `#${clr}`);
    } else {
      root.style.removeProperty("--client-clr");
    }
    return () => root.style.removeProperty("--client-clr");
  }, [profile?.client_clr]);

  return (
    <div className="dash">
      <header className="topbar">
        <div className="brand" data-testid="brand">
          {profile?.client_logo_url ? (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${profile.client_logo_url}`}
              alt={profile.client_name || "client logo"}
              className="client-logo-img"
              data-testid="client-logo"
            />
          ) : (
            <span className="brand-icon"><SangriaIcon size={26} /></span>
          )}
          <span data-testid="client-name">{profile?.client_name || APP_NAME}</span>
        </div>

        <nav className="topbar-nav" data-testid="topbar-nav">
          {canSeeEvents && (
            <button
              className={`nav-link ${tab === "calendar" ? "active" : ""}`}
              data-testid="menu-calendar"
              onClick={() => goTo("calendar")}
            >Events</button>
          )}
          {canSeeBudget && (
            <button
              className={`nav-link ${tab === "budget" ? "active" : ""}`}
              data-testid="menu-budget"
              onClick={() => goTo("budget")}
            >Budgets</button>
          )}
          {canSeeLedgers && (
            <button
              className={`nav-link ${tab === "ledgers" ? "active" : ""}`}
              data-testid="menu-ledgers"
              onClick={() => goTo("ledgers")}
            >Ledgers</button>
          )}
          {canSeeTxn && (
            <button
              className={`nav-link ${tab === "browse" ? "active" : ""}`}
              data-testid="menu-transactions"
              onClick={() => goTo("browse")}
            >Transactions</button>
          )}
          {canSeeCodes && (
            <button
              className={`nav-link ${tab === "codes" ? "active" : ""}`}
              data-testid="menu-codes"
              onClick={() => goTo("codes")}
            >Codes</button>
          )}
          {canSeePayees && (
            <button
              className={`nav-link ${tab === "payees" ? "active" : ""}`}
              data-testid="menu-payees"
              onClick={() => goTo("payees")}
            >Payees</button>
          )}
          {canSeePayments && (
            <button
              className={`nav-link ${tab === "payments" ? "active" : ""}`}
              data-testid="menu-payments"
              onClick={() => goTo("payments")}
            >Payments</button>
          )}
          {canSeeClients && (
            <button
              className={`nav-link ${tab === "clients" ? "active" : ""}`}
              data-testid="menu-clients"
              onClick={() => goTo("clients")}
            >Clients</button>
          )}
          {canSeeUsers && (
            <button
              className={`nav-link ${tab === "users" ? "active" : ""}`}
              data-testid="menu-users"
              onClick={() => goTo("users")}
            >Users</button>
          )}
          {canSeeFun && (
            <button
              className={`nav-link ${tab === "functions" ? "active" : ""}`}
              data-testid="menu-functions"
              onClick={() => goTo("functions")}
            >Functions</button>
          )}
          {canSeeSql && (
            <button
              className={`nav-link ${tab === "sql" ? "active" : ""}`}
              data-testid="menu-sql"
              onClick={() => goTo("sql")}
            >SQL Workbench</button>
          )}
        </nav>

        <div className="user-chip" style={{ position: "relative", marginLeft: "auto" }}>
          <button
            className="user-avatar"
            data-testid="profile-menu-btn"
            onClick={() => setMenuOpen((s) => !s)}
            title={displayName}
          >
            <span data-testid="user-initials">
              {(profile?.first_name?.[0] || "?").toUpperCase()}
              {(profile?.last_name?.[0]  || "").toUpperCase()}
            </span>
          </button>
          {menuOpen && (
            <div
              className="profile-menu"
              onMouseLeave={() => setMenuOpen(false)}
              data-testid="profile-menu"
            >
              <button
                className="profile-menu-item"
                data-testid="menu-changepw"
                onClick={() => { setMenuOpen(false); setShowPwChange(true); }}
              >Change my password</button>
              <button
                className="profile-menu-item danger"
                data-testid="logout-btn"
                onClick={logout}
              >Sign out</button>
            </div>
          )}
        </div>
      </header>

      <main className="dash-main">
        {tab === "calendar" && <CalendarList profile={profile} canWrite={canWriteEvents} canAdmin={canAdminEvents} />}
        {tab === "browse" && (
          <BrowserTab
            fys={fys}
            calSeq={calSeq}
            setCalSeq={setCalSeq}
            loadingFys={loadingFys}
            refreshFys={loadFys}
            isAdmin={isAdmin}
            profile={profile}
          />
        )}
        {tab === "budget" && <Budget />}
        {tab === "codes" && <CodesList profile={profile} canWrite={canWriteCodes} />}
        {tab === "payees" && <PayeesList profile={profile} canWrite={canWritePayees} />}
        {tab === "payments" && <PaymentsList profile={profile} canWrite={canWritePayments} />}
        {tab === "functions" && canSeeFun && <FunctionsList canWrite={canWriteFun} />}
        {tab === "ledgers" && canSeeLedgers && <AdminCategories standalone canWrite={canWriteLedgers} />}
        {tab === "users"   && canSeeUsers   && <AdminUsers standalone currentUserId={profile?.user_id} currentUserClient={profile?.client || ""} canWrite={canWriteUsers} />}
        {tab === "clients" && canSeeClients && <AdminClients standalone canWrite={canWriteClients} />}
        {tab === "sql"     && canSeeSql     && <SqlWorkbench />}
        {tab === "none" && (
          <section>
            <div className="card" style={{ padding: 40, textAlign: "center" }} data-testid="no-access-card">
              You don't have access to any page yet. Ask an admin to grant you access.
            </div>
          </section>
        )}
      </main>

      {showPwChange && (
        <ChangePasswordModal onClose={() => setShowPwChange(false)} />
      )}
    </div>
  );
}
